import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { signOutAndClear } from '../Module/Auth/Presentation/authSlice';
import { useAppDispatch } from '../store/hooks';
import {
  ACCESS_TOKEN_STORAGE_EVENT,
  getAccessToken,
  getRemainingAccessTokenMs,
} from '../core/api/tokenStorage';
import { PATHS } from './paths';

const PUBLIC_PATHS = new Set([
  PATHS.LOGIN,
  PATHS.PASSWORD_FIND,
  PATHS.DONATION_HISTORY_FIND,
  PATHS.SIGNUP,
]);

const SessionTimeoutHandler = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('translation');
  const timeoutRef = useRef<number | null>(null);
  const handledExpiryRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleSessionExpired = useCallback(() => {
    if (handledExpiryRef.current) return;

    handledExpiryRef.current = true;
    dispatch(signOutAndClear());

    if (!PUBLIC_PATHS.has(location.pathname)) {
      window.alert(t('page.login.messages.sessionExpired'));
      navigate(PATHS.LOGIN, { replace: true, state: { from: location } });
    }
  }, [dispatch, location, navigate, t]);

  const syncSessionTimeout = useCallback(() => {
    clearTimer();

    const token = getAccessToken();
    if (!token) {
      const storedUserId = window.localStorage.getItem('missionfund.userId');
      if (storedUserId) {
        if (PUBLIC_PATHS.has(location.pathname)) {
          dispatch(signOutAndClear());
        } else {
          handleSessionExpired();
        }
        return;
      }

      handledExpiryRef.current = false;
      return;
    }

    const remainingMs = getRemainingAccessTokenMs();
    if (remainingMs <= 0) {
      handleSessionExpired();
      return;
    }

    handledExpiryRef.current = false;
    timeoutRef.current = window.setTimeout(() => {
      handleSessionExpired();
    }, remainingMs);
  }, [clearTimer, dispatch, handleSessionExpired, location.pathname]);

  useEffect(() => {
    syncSessionTimeout();

    const handleTokenChanged = () => {
      syncSessionTimeout();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key && !event.key.startsWith('missionfund.')) return;
      syncSessionTimeout();
    };

    window.addEventListener(ACCESS_TOKEN_STORAGE_EVENT, handleTokenChanged);
    window.addEventListener('storage', handleStorage);

    return () => {
      clearTimer();
      window.removeEventListener(ACCESS_TOKEN_STORAGE_EVENT, handleTokenChanged);
      window.removeEventListener('storage', handleStorage);
    };
  }, [clearTimer, syncSessionTimeout]);

  useEffect(() => {
    syncSessionTimeout();
  }, [location.pathname, syncSessionTimeout]);

  return null;
};

export default SessionTimeoutHandler;
